import React, { useEffect, useRef } from 'react';

export const MasterToolbar = ({
  searchTerm = '',
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search records... (Ctrl+K)',
  filters = [],
  activeChips = [],
  onRemoveChip,
  onClearAllChips,
  onClearAllFilters,
  onRefresh,
  refreshing = false,
  loading = false
}) => {
  const searchInputRef = useRef(null);
  const currentSearch = searchValue !== undefined ? searchValue : searchTerm;
  const isRefreshing = refreshing || loading;
  const handleClearAll = onClearAllChips || onClearAllFilters;

  // Global Ctrl+K shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="mst-toolbar">
      <div className="mst-toolbar-row">
        <div className="mst-toolbar-left">
          {/* Search Box */}
          <div className="mst-search-group">
            <svg
              className="mst-search-icon"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              style={{ width: '14px', height: '14px', minWidth: '14px', maxWidth: '14px', flexShrink: 0 }}
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              className="mst-search-input"
              placeholder={searchPlaceholder}
              value={currentSearch}
              onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
            />
            {!currentSearch ? (
              <span className="mst-search-kbd">Ctrl K</span>
            ) : (
              <button
                type="button"
                className="mst-search-clear"
                onClick={() => onSearchChange && onSearchChange('')}
                title="Clear search query"
              >
                ×
              </button>
            )}
          </div>

          {/* Compact Inline Filter Dropdowns */}
          {filters && filters.map((f) => (
            <select
              key={f.id}
              id={f.id}
              className="mst-filter-select"
              value={f.value}
              onChange={(e) => f.onChange(e.target.value)}
              title={f.label}
            >
              {f.options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          ))}
        </div>

        <div className="mst-toolbar-right">
          {onRefresh && (
            <button
              type="button"
              className={`mst-btn-icon ${isRefreshing ? 'spinning' : ''}`}
              onClick={onRefresh}
              title="Refresh data"
              disabled={isRefreshing}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="23 4 23 10 17 10" />
                <polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Active Filter Chips */}
      {activeChips && activeChips.length > 0 && (
        <div className="mst-chips-bar">
          <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 500 }}>Active Filters:</span>
          {activeChips.map((chip) => {
            const displayText = chip.valueText ? `${chip.label}: ${chip.valueText}` : chip.label;
            const removeFn = chip.onRemove || (onRemoveChip ? () => onRemoveChip(chip.id) : null);
            return (
              <span key={chip.id} className="mst-chip">
                <span>{displayText}</span>
                {removeFn && (
                  <button
                    type="button"
                    className="mst-chip-remove"
                    onClick={removeFn}
                    title="Remove filter"
                  >
                    ×
                  </button>
                )}
              </span>
            );
          })}

          {handleClearAll && (
            <button
              type="button"
              className="mst-clear-all-btn"
              onClick={handleClearAll}
            >
              Clear all
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default MasterToolbar;
